import { createContext, ReactNode, useCallback, useContext, useState } from 'react';
import { ValidateTestCodeResponse } from '@/features/Login/services/validateTestCode.ts';


export type Student = {
  _id: string
  name: string
  birthDate: Date
  company: { name: string }
}

export type OnboardingAnswer = {
  question: string
  answer: string
}

export type Configurations = {
  showOnboardingQuestions: boolean
  showTestResultToStudent: boolean
  showHubspotForm: string
  redirectUrl?: string
  shouldRecordVideo: boolean
}

type AuthContextType = {
  // Real-time communication
  realtimeToken: string | null
  realtimeChannel: string | null
  code: string

  // Student data
  student: Student | null

  configurations: Configurations

  resetTestData: () => void
  handleTestCodeValidated: (
    code: string,
    data: ValidateTestCodeResponse
  ) => void
}

const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [code, setCode] = useState<string>('')
  const [realtimeToken, setRealtimeToken] = useState<string | null>(null)
  const [realtimeChannel, setRealtimeChannel] = useState<string | null>(null)
  const [student, setStudent] = useState<Student | null>(null)
  const [configurations, setConfigurations] = useState<Configurations>({
    showOnboardingQuestions: false,
    showTestResultToStudent: true,
    showHubspotForm: '',
    shouldRecordVideo: false
  })

  const resetTestData = () => {
    setRealtimeToken(null)
    setRealtimeChannel(null)
    sessionStorage.removeItem('accessToken')
    sessionStorage.removeItem('testCode')
  }

  const handleTestCodeValidated = useCallback(
    async (code: string, data: ValidateTestCodeResponse) => {
      sessionStorage.setItem('accessToken', data.accessToken)
      sessionStorage.setItem('testCode', code)

      setCode(code)
      setRealtimeChannel(data.realtimeChannel)
      setRealtimeToken(data.realtimeMessageToken)
      setStudent(data.student)
      setConfigurations((c) => ({
        ...c,
        showHubspotForm: data.showHubspotForm,
        showTestResultToStudent: data.showTestResultToStudent,
        showOnboardingQuestions: data.showOnboardingQuestions,
        shouldRecordVideo: data.shouldRecordVideo,
        redirectUrl: data.redirectUrl
      }))
    },
    []
  )

  return (
    <AuthContext.Provider
      value={{
        code,
        realtimeToken,
        realtimeChannel,
        student,
        configurations,
        resetTestData,
        handleTestCodeValidated
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useTestData must be used within TestDataProvider')
  }
  return context
}
